import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { AccountService } from '@services/api/account.service';
import { CustomValidator } from '@utils/custom-validators';
import { firstValueFrom } from 'rxjs';

/** ユーザー設定のビューモデル */
export type UserSettingViewModel = {
  /** メールアドレス */
  email: string;
  /** 2段階認証必須か
   * 呼び出し元コンポーネントへの返り値には含まれない*/
  mfaRequired?: boolean;
  /** 2段階認証ステータス */
  mfaStatus: boolean;

  // ヘッダーからダイアログに値は送信しないが、ダイアログからヘッダーに値を送信するもの
  password?: string;
  oldPassword?: string;
  passwordConfirm?: string;
};

/** ユーザー設定のフォームグループ */
export type UserSettingFormGroup = FormGroup<{
  /** メールアドレス */
  email: FormControl<string>;
  /** 現在のパスワード */
  oldPassword: FormControl<string>;
  /** 新しいパスワード */
  password: FormControl<string>;
  /** 確認用パスワード */
  passwordConfirm: FormControl<string>;
  /** 2要素認証ステータス */
  mfaStatus: FormControl<boolean>;
}>;

@Component({
  selector: 'app-user-setting',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
  ],
  templateUrl: './user-setting.component.html',
  styleUrl: './user-setting.component.scss',
})
export class UserSettingComponent {
  public updating: boolean = false;
  public email: string = '';
  public password: string = '';
  public mfaStatus: boolean = true;
  public mfaRequired: boolean = true;
  /** フォームグループ */
  public formGroup: UserSettingFormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: UserSettingViewModel,
    private accountService: AccountService,
    private dialogRef: MatDialogRef<UserSettingComponent>,
    private dialog: MatDialog,
  ) {
    this.email = options.email;
    this.password = '';
    this.mfaStatus = options.mfaStatus;
    this.mfaRequired = options.mfaRequired ?? true;

    this.formGroup = new FormGroup(
      {
        email: new FormControl(options.email || '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        oldPassword: new FormControl('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        password: new FormControl('', {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.minLength(8),
            Validators.maxLength(32),
            // 記号: ^$*.[]{}()?"!@#%&/\,><':;|_~`
            // https://docs.aws.amazon.com/ja_jp/cognito/latest/developerguide/user-pool-settings-policies.html
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`])[!-~]+$/,
            ),
          ],
        }),
        passwordConfirm: new FormControl('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        mfaStatus: new FormControl(options.mfaStatus || false, {
          nonNullable: true,
          validators: [Validators.required],
        }),
      },
      {
        validators: [
          CustomValidator.matchPassword(),
          CustomValidator.diffPassword(),
        ],
      },
    );
    // 新しいパスワードと確認用パスワードが一致するかチェック;
  }

  // 編集画面を開く
  openEditDialog(): void {
    this.updating = true;
  }
  // 表示画面を開く
  openDisplayDialog(): void {
    this.updating = false;
  }

  // ユーザー設定の更新
  async submit(): Promise<void> {
    // 確認ダイアログの表示
    const operation = '変更';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `ユーザー設定を${operation}してよろしいですか？`,
      operation: operation,
    };
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    // 更新
    const userSetting: UserSettingViewModel = {
      email: this.formGroup.get('email')?.value as string,
      oldPassword: this.formGroup.get('oldPassword')?.value as string,
      password: this.formGroup.get('password')?.value as string,
      passwordConfirm: this.formGroup.get('passwordConfirm')?.value as string,
      mfaStatus: this.formGroup.get('mfaStatus')?.value as boolean,
    };
    this.dialogRef.close(userSetting);
  }

  // MFA有効化
  public enableMFA(): void {
    this.accountService.enableMfa().subscribe({
      next: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'info',
          message: `2要素認証を必須に設定しました。\n次回ログインから2要素認証が必要になります。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        this.dialogRef.close();
      },
    });
  }

  // MFA有効化
  public disableMFA(): void {
    this.accountService.disableMfa().subscribe({
      next: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'info',
          message: `2要素認証の設定を解除しました。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        this.dialogRef.close();
      },
    });
  }
}
